import React, { useState } from "react";
import { toast } from "sonner";
import { finApi, finErr, money } from "@/lib/financeApi";
import { useIsSuper } from "@/components/nds/windows/finance/InvoiceActions";
import { btnGold } from "@/components/nds/windows/finance/parts";

const LABEL = { received: "Received", pending_employer: "Awaiting employer", not_applicable: "Not a collection" };

function ReviewRow({ x, onDone }) {
  const [busy, setBusy] = useState(false);
  const decide = async (status) => {
    const note = window.prompt(`Mark as ${LABEL[status]} · why? (at least 5 letters)`, "");
    if (note === null) return;
    setBusy(true);
    try { await finApi.post("/funding/override", { txn_id: x.txn_id, status, note }); toast.success(`Marked ${LABEL[status]}`); onDone(x.txn_id); }
    catch (e) { toast.error(finErr(e)); }
    setBusy(false);
  };
  const b = (st, text) => <button type="button" disabled={busy} onClick={() => decide(st)} data-testid={`funding-review-${st}-${x.txn_id}`} className={`${btnGold} !py-0.5`}>{text}</button>;
  return (
    <li data-testid={`funding-review-row-${x.txn_id}`} className="flex flex-wrap items-center gap-2 py-1 border-t border-white/5 text-nex-muted">
      <span className="basis-full sm:basis-auto sm:flex-1 min-w-0 break-words"><span className="text-nex-silver">{x.resident}</span> · {x.created_at?.slice(0, 10)} · {x.bucket === "escrow" ? "Rent" : "Case mgmt"} <span className="font-mono">{money(x.amount)}</span> · {x.reason}</span>
      {b("received", "Received")}{b("pending_employer", "Awaiting employer")}
    </li>
  );
}

/** Super admin: one-time funding backfill — dry run first, then confirm; unmatched rows wait for a decision. */
export function FundingBackfill() {
  const sa = useIsSuper();
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  if (!sa) return null;
  const run = async (confirm) => {
    setBusy(true);
    try {
      const r = confirm ? await finApi.post("/funding/backfill", {}) : await finApi.get("/funding/backfill");
      setD(r);
      if (confirm) toast.success(`Backfill done · ${r.changed} row(s) updated`);
    } catch (e) { toast.error(finErr(e)); }
    setBusy(false);
  };
  const decided = () => finApi.get("/funding/backfill").then(setD).catch((e) => toast.error(finErr(e)));
  return (
    <div data-testid="funding-backfill" className="p-3 rounded-md border border-white/10 bg-white/[0.02] space-y-2 text-xs">
      <div className="flex flex-wrap items-center gap-2">
        <span className="nds-label-dim text-[9px]">Funding status backfill · super admin</span>
        <button type="button" disabled={busy} onClick={() => run(false)} data-testid="funding-backfill-dry" className={btnGold}>{busy && !d ? "Checking…" : "Dry run"}</button>
        {d?.dry_run && <button type="button" disabled={busy} onClick={() => run(true)} data-testid="funding-backfill-confirm" className={btnGold}>Confirm · write {d.would_change} change(s)</button>}
        {d?.done && <span className="text-emerald-300" data-testid="funding-backfill-done">Backfill has run before — a new run only fixes rows that changed, never your decisions</span>}
      </div>
      {d && <div className="flex flex-wrap gap-x-4 gap-y-1" data-testid="funding-backfill-totals">{Object.entries(d.totals).map(([k, v]) => <span key={k} className="text-nex-silver" data-testid={`funding-backfill-total-${k}`}>{LABEL[k]}: {v.rows} rows · {money(v.amount)}</span>)}</div>}
      {d && (d.needs_review_count > 0 ? <details data-testid="funding-backfill-review"><summary className="cursor-pointer text-amber-300">{d.needs_review_count} row(s) couldn&apos;t be matched to an invoice · held as Awaiting employer until you decide{d.needs_review_count > d.needs_review.length ? ` · showing first ${d.needs_review.length}` : ""}</summary>
        <ul className="mt-1 max-h-64 overflow-y-auto">{d.needs_review.map((x) => <ReviewRow key={x.txn_id} x={x} onDone={decided} />)}</ul></details>
        : <div className="text-emerald-300" data-testid="funding-backfill-review-empty">Every row is matched or decided — nothing to review.</div>)}
    </div>
  );
}
